import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { PortalDsField, PortalDsSelect } from "src/components/portal-design-system/portal-ds-field";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import type { LoadCommissionInvoicesRequest } from "../types/commissions.types";

const QUARTER_OPTIONS = [
  { value: "Q1", label: "Q1 (Jan–Mar)" },
  { value: "Q2", label: "Q2 (Apr–Jun)" },
  { value: "Q3", label: "Q3 (Jul–Sep)" },
  { value: "Q4", label: "Q4 (Oct–Dec)" },
];

function getCurrentQuarter(): string {
  const month = new Date().getMonth() + 1;
  return `Q${Math.ceil(month / 3)}`;
}

function getYearsForLoad(): number[] {
  const currentYear = new Date().getFullYear();
  return [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];
}

interface LoadCommissionInvoicesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: LoadCommissionInvoicesRequest) => Promise<void>;
  /** Quarter/year currently in view on the Commissions tab; falls back to calendar quarter. */
  defaultQuarter?: string;
  defaultYear?: number;
}

const LoadCommissionInvoicesModal: React.FC<LoadCommissionInvoicesModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultQuarter,
  defaultYear,
}) => {
  const yearOptions = useMemo(
    () => getYearsForLoad().map((y) => ({ value: String(y), label: String(y) })),
    []
  );
  const [quarter, setQuarter] = useState(getCurrentQuarter);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setQuarter(defaultQuarter ?? getCurrentQuarter());
      setYear(String(defaultYear ?? new Date().getFullYear()));
    }
  }, [open, defaultQuarter, defaultYear]);

  const handleSubmit = async() => {
    if (!quarter || !year || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({ quarter, year: Number(year) });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = Boolean(quarter && year) && !isSubmitting;
  const loadPeriodLabel = `${quarter} ${year}`;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="bg-white sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className={portalDs.sectionTitle}>
            Load commission invoices
          </DialogTitle>
          <DialogDescription className={portalDs.sheetDescription}>
            Select the quarter and year to load paid commission invoices from QuickBooks.
          </DialogDescription>
        </DialogHeader>
        <div
          className="rounded-md border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3 text-sm text-[#1A1D26]"
          role="status"
        >
          You are about to load invoices for{" "}
          <span className="font-semibold">{loadPeriodLabel}</span>.
          Commission figures for this quarter will be recalculated; other quarters are not affected.
        </div>
        <div className="flex flex-col gap-4 py-2">
          <PortalDsField label="Quarter" htmlFor="load-invoices-quarter">
            <PortalDsSelect
              id="load-invoices-quarter"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
            >
              {QUARTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </PortalDsSelect>
          </PortalDsField>
          <PortalDsField label="Year" htmlFor="load-invoices-year">
            <PortalDsSelect
              id="load-invoices-year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {yearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </PortalDsSelect>
          </PortalDsField>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="primary"
            size="sm"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {isSubmitting ? "Loading…" : `Load ${loadPeriodLabel}`}
          </PortalDsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoadCommissionInvoicesModal;
